import React, { useEffect, useState } from 'react';
import { TentIcon, QrCodeIcon, PlusIcon, SearchIcon, FilterIcon, CalendarIcon, MapPinIcon, UsersIcon, ClockIcon, ArrowUpDownIcon, EyeIcon, EditIcon, TrashIcon, CheckIcon, XIcon, ArrowRightIcon, ExternalLinkIcon, Share2Icon, PresentationIcon, DollarSignIcon, PercentIcon } from 'lucide-react';
import { FairForm } from './FairForm';
// Mock data for agricultural fairs
const mockFairs = [{
  id: 1,
  name: 'Agri Expo 2025',
  location: 'Madrid, Spain',
  startDate: '2025-11-15',
  endDate: '2025-11-20',
  status: 'upcoming',
  attendees: 5000,
  description: 'The largest agricultural fair in Europe showcasing the latest irrigation technologies.',
  hasQRCode: false,
  promotionDiscount: 40
}, {
  id: 2,
  name: 'Farm Tech Show',
  location: 'Chicago, USA',
  startDate: '2025-12-05',
  endDate: '2025-12-10',
  status: 'upcoming',
  attendees: 8000,
  description: 'An international exhibition of agricultural machinery and equipment.',
  hasQRCode: false,
  promotionDiscount: 40
}, {
  id: 3,
  name: 'Irrigation Conference 2025',
  location: 'Dubai, UAE',
  startDate: '2025-10-12',
  endDate: '2025-10-15',
  status: 'upcoming',
  attendees: 3000,
  description: 'A specialized conference focused on innovative irrigation solutions for arid regions.',
  hasQRCode: false,
  promotionDiscount: 40
}, {
  id: 4,
  name: 'AgroTech Summit',
  location: 'Berlin, Germany',
  startDate: '2025-02-18',
  endDate: '2025-02-22',
  status: 'upcoming',
  attendees: 6000,
  description: 'A summit bringing together agricultural technology innovators and farmers.',
  hasQRCode: false,
  promotionDiscount: 40
}, {
  id: 5,
  name: 'Paris Agricultural Show (SIA)',
  location: 'Paris, France',
  startDate: '2025-02-24',
  endDate: '2025-03-03',
  status: 'upcoming',
  attendees: 12000,
  description: 'One of the most important agricultural events in France, showcasing livestock, crops, and agricultural machinery.',
  hasQRCode: false,
  promotionDiscount: 40
}, {
  id: 6,
  name: 'Agritechnica',
  location: 'Hannover, Germany',
  startDate: '2025-11-12',
  endDate: '2025-11-18',
  status: 'upcoming',
  attendees: 15000,
  description: "The world's leading trade fair for agricultural machinery and equipment.",
  hasQRCode: false,
  promotionDiscount: 40
}, {
  id: 7,
  name: 'World Ag Expo',
  location: 'Tulare, USA',
  startDate: '2025-02-13',
  endDate: '2025-02-15',
  status: 'upcoming',
  attendees: 10000,
  description: 'The largest annual agricultural exposition in the world with over 1,400 exhibitors.',
  hasQRCode: false,
  promotionDiscount: 40
}, {
  id: 8,
  name: 'International Green Week',
  location: 'Berlin, Germany',
  startDate: '2025-01-19',
  endDate: '2025-01-28',
  status: 'upcoming',
  attendees: 8500,
  description: 'A unique exhibition for the food, agriculture, and horticulture industries.',
  hasQRCode: false,
  promotionDiscount: 40
}, {
  id: 9,
  name: 'EIMA International',
  location: 'Bologna, Italy',
  startDate: '2025-11-06',
  endDate: '2025-11-10',
  status: 'upcoming',
  attendees: 9000,
  description: 'An international exhibition of machinery for agriculture and gardening.',
  hasQRCode: false,
  promotionDiscount: 40
}];
export function FairsManagement({
  onGenerateQR
}) {
  const [fairs, setFairs] = useState(mockFairs);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('startDate');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isAddingFair, setIsAddingFair] = useState(false);
  const [isEditingFair, setIsEditingFair] = useState(false);
  const [currentFair, setCurrentFair] = useState(null);
  // Filter and sort fairs
  const filteredFairs = fairs.filter(fair => {
    const matchesSearch = fair.name.toLowerCase().includes(searchQuery.toLowerCase()) || fair.location.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || fair.status === filterStatus;
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    let comparison = 0;
    if (sortBy === 'name') {
      comparison = a.name.localeCompare(b.name);
    } else if (sortBy === 'location') {
      comparison = a.location.localeCompare(b.location);
    } else if (sortBy === 'startDate') {
      comparison = new Date(a.startDate) - new Date(b.startDate);
    } else if (sortBy === 'attendees') {
      comparison = a.attendees - b.attendees;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  const handleSort = column => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };
  const handleAddFair = fair => {
    setFairs([...fairs, {
      ...fair,
      id: fairs.length + 1,
      hasQRCode: false,
      promotionDiscount: 40
    }]);
    setIsAddingFair(false);
  };
  const handleEditFair = fair => {
    setFairs(fairs.map(f => f.id === fair.id ? fair : f));
    setIsEditingFair(false);
    setCurrentFair(null);
  };
  const handleDeleteFair = id => {
    if (window.confirm('Are you sure you want to delete this fair?')) {
      setFairs(fairs.filter(fair => fair.id !== id));
    }
  };
  const handleGenerateQR = fair => {
    if (onGenerateQR) {
      onGenerateQR(fair);
    }
  };
  // Format date range for display
  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    // Format options
    const options = {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    };
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
  };
  // Calculate fair status
  const getFairStatus = (startDate, endDate) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (now < start) return 'upcoming';
    if (now >= start && now <= end) return 'active';
    return 'past';
  };
  // Get status badge color
  const getStatusColor = status => {
    switch (status) {
      case 'upcoming':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'past':
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default:
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    }
  };
  if (isAddingFair) {
    return <FairForm onSubmit={handleAddFair} onCancel={() => setIsAddingFair(false)} data-id="element-148" data-mp="3c65snfly0qs7" />;
  }
  if (isEditingFair && currentFair) {
    return <FairForm fair={currentFair} onSubmit={handleEditFair} onCancel={() => {
      setIsEditingFair(false);
      setCurrentFair(null);
    }} data-id="element-149" data-mp="336sdc6b517zk" />;
  }
  return <div data-id="element-150" data-mp="2y764sa698fx3">
      <div className="flex justify-between items-center mb-6" data-id="element-151" data-mp="2y6xn84z6smfk">
        <div data-id="element-152" data-mp="2t9nry8p49gjt">
          <h2 className="text-2xl font-bold text-white flex items-center" data-id="element-153" data-mp="2t9xhxwa5vnpi">
            <TentIcon className="mr-2" size={24} data-id="element-154" data-mp="18rd7mcrvxkgj" />
            Agricultural Fairs Management
          </h2>
          <p className="text-blue-300 mt-1" data-id="element-155" data-mp="1dolu2pk2ganl">
            Register and manage agricultural fairs, generate promotional QR
            codes
          </p>
        </div>
        <button onClick={() => setIsAddingFair(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors shadow-md flex items-center" data-id="element-156" data-mp="1mqd0jwmfklii">
          <PlusIcon size={18} className="mr-2" data-id="element-157" data-mp="pxzn71lsswf6" />
          Register Fair
        </button>
      </div>
      {/* Filters and search */}
      <div className="bg-white/5 backdrop-blur-sm border border-blue-800/30 rounded-xl p-4 mb-6" data-id="element-158" data-mp="z08ulilrs9oc">
        <div className="flex flex-wrap items-center gap-4" data-id="element-159" data-mp="13s0nhzseerhn">
          <div className="flex-1 min-w-[240px]" data-id="element-160" data-mp="13tlhnoasw8ez">
            <div className="relative" data-id="element-161" data-mp="7ae61o5qko0q">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-300" size={18} data-id="element-162" data-mp="79ciwlbg4b2n" />
              <input type="text" placeholder="Search fairs..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-white/10 border border-blue-800/30 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-163" data-mp="b8tnzdmyn67a" />
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-164" data-mp="35rtbrdjv0mm3">
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="bg-white/10 border border-blue-800/30 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none" data-id="element-165" data-mp="35r1iwahnd2zi">
              <option value="all" data-id="element-166" data-mp="3tign68szhude">All Statuses</option>
              <option value="upcoming" data-id="element-167" data-mp="3th5mwiyqz4r1">Upcoming</option>
              <option value="active" data-id="element-168" data-mp="3oikis8gdk0xb">Active</option>
              <option value="past" data-id="element-169" data-mp="3okx6z3cq77it">Past</option>
            </select>
            <button className="flex items-center gap-2 bg-white/10 text-blue-300 hover:bg-white/20 px-4 py-2.5 rounded-lg transition-colors" data-id="element-170" data-mp="6e0ozlljzf1y">
              <FilterIcon size={18} data-id="element-171" data-mp="kzwqpbytqd2c" />
              More Filters
            </button>
          </div>
        </div>
      </div>
      {/* Fairs Table */}
      <div className="bg-white/5 backdrop-blur-sm border border-blue-800/30 rounded-xl shadow-xl overflow-hidden" data-id="element-172" data-mp="pxpxa641h935">
        <div className="overflow-x-auto" data-id="element-173" data-mp="byyl4p8qlxhn">
          <table className="w-full" data-id="element-174" data-mp="gy2upgzkpx8i">
            <thead data-id="element-175" data-mp="gx0316uffqrj">
              <tr className="border-b border-blue-800/30" data-id="element-176" data-mp="gwi05rd9eean">
                <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-177" data-mp="296lhgls6e6b">
                  <button className="flex items-center focus:outline-none" onClick={() => handleSort('name')} data-id="element-178" data-mp="70x2u6j4y278">
                    Fair Name
                    <ArrowUpDownIcon size={14} className={`ml-1 ${sortBy === 'name' ? 'text-blue-400' : 'text-blue-500/50'}`} data-id="element-179" data-mp="3pcbdxyg7hbo0" />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-180" data-mp="3fggp2lle2kcs">
                  <button className="flex items-center focus:outline-none" onClick={() => handleSort('location')} data-id="element-181" data-mp="3kh4gh8dj537j">
                    Location
                    <ArrowUpDownIcon size={14} className={`ml-1 ${sortBy === 'location' ? 'text-blue-400' : 'text-blue-500/50'}`} data-id="element-182" data-mp="2ot7b1wf9pg3v" />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-183" data-mp="2frzogbm463pm">
                  <button className="flex items-center focus:outline-none" onClick={() => handleSort('startDate')} data-id="element-184" data-mp="33h21t79ptaeb">
                    Date Range
                    <ArrowUpDownIcon size={14} className={`ml-1 ${sortBy === 'startDate' ? 'text-blue-400' : 'text-blue-500/50'}`} data-id="element-185" data-mp="38fvgh30qn7g2" />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-186" data-mp="2ypkcjgh20y96">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-187" data-mp="3r767dk6grk7y">
                  <button className="flex items-center focus:outline-none" onClick={() => handleSort('attendees')} data-id="element-188" data-mp="1frnq2x0kqgzg">
                    Expected Attendees
                    <ArrowUpDownIcon size={14} className={`ml-1 ${sortBy === 'attendees' ? 'text-blue-400' : 'text-blue-500/50'}`} data-id="element-189" data-mp="1ar026k7n6g9g" />
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-190" data-mp="1kjfk5hixv4uc">
                  QR Code
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-blue-300 uppercase tracking-wider" data-id="element-191" data-mp="rurtf6ff7n9e">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-blue-800/30" data-id="element-192" data-mp="10v6jsk78rsjr">
              {filteredFairs.map(fair => <tr key={fair.id} className="hover:bg-blue-800/20 transition-colors" data-id="element-193" data-mp="2h96cpb7o6hxi">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-194" data-mp="2hazof4x53scd">
                    <div className="flex items-center" data-id="element-195" data-mp="2c9k0n53kv7qa">
                      <div className="flex-shrink-0 h-10 w-10 bg-amber-900/30 rounded-full flex items-center justify-center border border-amber-700/30" data-id="element-196" data-mp="19wxhnn5nyp1h">
                        <TentIcon className="h-5 w-5 text-amber-500" aria-hidden="true" data-id="element-197" data-mp="1etxfxbw9uni1" />
                      </div>
                      <div className="ml-4" data-id="element-198" data-mp="1iviz1ezefy0o">
                        <div className="text-sm font-medium text-white" data-id="element-199" data-mp="1nwoql9hbpf8m">
                          {fair.name}
                        </div>
                        <div className="text-xs text-blue-300" data-id="element-200" data-mp="r4332ubycyxs">
                          ID: FAIR-{fair.id.toString().padStart(4, '0')}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-201" data-mp="14y46i1duna4h">
                    <div className="flex items-center text-sm text-white" data-id="element-202" data-mp="14znn5t3f3n6s">
                      <MapPinIcon size={16} className="mr-1 text-blue-400" data-id="element-203" data-mp="7hnj440o7l8e" />
                      {fair.location}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-204" data-mp="4yaeic8orhzy">
                    <div className="flex items-center text-sm text-white" data-id="element-205" data-mp="4vei4schl1dm">
                      <CalendarIcon size={16} className="mr-1 text-blue-400" data-id="element-206" data-mp="3w4ldm2u4y8on" />
                      {formatDateRange(fair.startDate, fair.endDate)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-207" data-mp="3n2u38konhdog">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(fair.status)}`} data-id="element-208" data-mp="3i4ka0fmhkn5h">
                      {fair.status === 'upcoming' && <ClockIcon size={12} className="mr-1" data-id="element-209" data-mp="38e7qmqqer00x" />}
                      {fair.status === 'active' && <CheckIcon size={12} className="mr-1" data-id="element-210" data-mp="33fe9mobwwqw0" />}
                      {fair.status === 'past' && <XIcon size={12} className="mr-1" data-id="element-211" data-mp="33djszm7obkxq" />}
                      {fair.status.charAt(0).toUpperCase() + fair.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-212" data-mp="2uh2d65od6yvn">
                    <div className="flex items-center text-sm text-white" data-id="element-213" data-mp="ycuv8j7ugi68">
                      <UsersIcon size={16} className="mr-1 text-blue-400" data-id="element-214" data-mp="12desrnwbxnsw" />
                      {fair.attendees.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-215" data-mp="tklaripi0abc">
                    {fair.hasQRCode ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30" data-id="element-216" data-mp="tiidcdm0newl">
                        <CheckIcon size={12} className="mr-1" data-id="element-217" data-mp="jo8gaim4psfj" />
                        Generated
                      </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30" data-id="element-218" data-mp="5oqnls5ppv0w">
                        <XIcon size={12} className="mr-1" data-id="element-219" data-mp="201ylxtswcora" />
                        Not Generated
                      </span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-220" data-mp="24xnrcdrlkzvi">
                    <div className="flex justify-end space-x-2" data-id="element-221" data-mp="2f1kmxjryfgi4">
                      {!fair.hasQRCode && <button onClick={() => handleGenerateQR(fair)} className="text-purple-400 hover:text-purple-300 bg-purple-900/30 hover:bg-purple-800/50 px-3 py-1.5 rounded-md transition-colors inline-flex items-center" data-id="element-222" data-mp="2a6orsarf8d0s">
                          <QrCodeIcon size={16} className="mr-1" data-id="element-223" data-mp="2xug9jtvqdi9g" />
                          Generate QR
                        </button>}
                      {fair.hasQRCode && <button onClick={() => handleGenerateQR(fair)} className="text-purple-400 hover:text-purple-300 bg-purple-900/30 hover:bg-purple-800/50 px-3 py-1.5 rounded-md transition-colors inline-flex items-center" data-id="element-224" data-mp="2sutsf9cs7hvd">
                          <EyeIcon size={16} className="mr-1" data-id="element-225" data-mp="32ros3vksjob8" />
                          View QR
                        </button>}
                      <button onClick={() => {
                    setCurrentFair(fair);
                    setIsEditingFair(true);
                  }} className="text-blue-400 hover:text-blue-300 bg-blue-900/30 hover:bg-blue-800/50 p-1.5 rounded-md transition-colors" data-id="element-226" data-mp="18g07bcrvorix">
                        <EditIcon size={16} data-id="element-227" data-mp="3cl64l4fatec5" />
                      </button>
                      <button onClick={() => handleDeleteFair(fair.id)} className="text-red-400 hover:text-red-300 bg-red-900/30 hover:bg-red-800/50 p-1.5 rounded-md transition-colors" data-id="element-228" data-mp="3hh4q8k98si8v">
                        <TrashIcon size={16} data-id="element-229" data-mp="3qikzgoobmebi" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {filteredFairs.length === 0 && <div className="text-center py-12" data-id="element-230" data-mp="45mf8ghr0g2d">
            <TentIcon size={48} className="mx-auto text-blue-800/50 mb-4" data-id="element-231" data-mp="92w4nw550rq7" />
            <h3 className="text-lg font-medium text-white mb-1" data-id="element-232" data-mp="e1zbnm4oh80t">
              No fairs found
            </h3>
            <p className="text-blue-300 mb-4" data-id="element-233" data-mp="i2sysdaaqisn">
              Try adjusting your search or filters
            </p>
            <button onClick={() => setIsAddingFair(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors shadow-md inline-flex items-center" data-id="element-234" data-mp="22mejdz1rl4ia">
              <PlusIcon size={18} className="mr-2" data-id="element-235" data-mp="1tk5crk9m9va6" />
              Register New Fair
            </button>
          </div>}
      </div>
      {/* Promotion Info */}
      <div className="mt-6 bg-gradient-to-r from-amber-600/20 to-amber-700/20 backdrop-blur-sm border border-amber-500/30 rounded-xl p-5 relative overflow-hidden" data-id="element-236" data-mp="1onojycrh2sqr">
        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full filter blur-3xl -z-10" data-id="element-237" data-mp="2hbkepbxheakw"></div>
        <div className="flex items-start" data-id="element-238" data-mp="2chomkq3wfhxn">
          <div className="p-3 bg-gradient-to-br from-amber-500/20 to-amber-700/20 rounded-lg inline-flex mb-4 mr-4" data-id="element-239" data-mp="27kpra48i14xv">
            <PercentIcon size={24} className="text-amber-400" data-id="element-240" data-mp="3503nrtocure1" />
          </div>
          <div data-id="element-241" data-mp="309diegfxiojc">
            <h3 className="text-lg font-bold text-white mb-1" data-id="element-242" data-mp="308u02ymd9y4s">
              Fair Promotion Details
            </h3>
            <p className="text-blue-200" data-id="element-243" data-mp="28kqze3q5liuo">
              When generating QR codes for fairs, a special 40% discount
              promotion will be automatically applied to any quotes created
              using the QR code.
            </p>
            <div className="mt-4 flex flex-wrap gap-4" data-id="element-244" data-mp="1zj18cz06olxf">
              <div className="bg-white/10 rounded-lg p-3 flex items-center" data-id="element-245" data-mp="23n90l65uok0e">
                <DollarSignIcon size={20} className="text-green-400 mr-2" data-id="element-246" data-mp="2rbiellwto3gz" />
                <div data-id="element-247" data-mp="2wb4s7wgkblqb">
                  <div className="text-sm text-white font-medium" data-id="element-248" data-mp="2wbd87x7zf75q">
                    40% Discount
                  </div>
                  <div className="text-xs text-blue-300" data-id="element-249" data-mp="2iakc5821840i">
                    Applied to all fair quotes
                  </div>
                </div>
              </div>
              <div className="bg-white/10 rounded-lg p-3 flex items-center" data-id="element-250" data-mp="16vnj7n2q8unv">
                <PresentationIcon size={20} className="text-blue-400 mr-2" data-id="element-251" data-mp="1bv9z649xwp0d" />
                <div data-id="element-252" data-mp="1bt6wokrlp3er">
                  <div className="text-sm text-white font-medium" data-id="element-253" data-mp="13e99ktwi71ah">
                    Display Mode
                  </div>
                  <div className="text-xs text-blue-300" data-id="element-254" data-mp="zdfm21mz3oi4">
                    Optimized for kiosk screens
                  </div>
                </div>
              </div>
              <div className="bg-white/10 rounded-lg p-3 flex items-center" data-id="element-255" data-mp="18dcg6is4tfkk">
                <Share2Icon size={20} className="text-purple-400 mr-2" data-id="element-256" data-mp="18b9h79904c3p" />
                <div data-id="element-257" data-mp="kojj2izrx1od">
                  <div className="text-sm text-white font-medium" data-id="element-258" data-mp="ko1ff1as7l6f">
                    Sharable Links
                  </div>
                  <div className="text-xs text-blue-300" data-id="element-259" data-mp="udkwc1exp4yn">
                    Easy to distribute
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}